import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingTitle } from '@/components/ui/page-heading';
import { BINARY_ANSWERS } from '@/constants/answers';
import type { InstitutionFuturesClientCategorisationAdditionalOrDefaults } from '@/features/institution/futures/client-categorisation/schema';
import { CLIENT_CATEGORISATION_SECONDARY_QUESTIONS_NO_COUNT_THRESHOLD } from '@/services/api/profile';

const InstitutionFuturesClientCategorisationFieldsetAdditionalLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.clientCategorisation.title')}</PageHeadingTitle>
      </legend>
    </PageHeading>
  );
};
InstitutionFuturesClientCategorisationFieldsetAdditionalLegend.displayName = 'InstitutionFuturesClientCategorisationFieldsetAdditionalLegend';

const InstitutionFuturesClientCategorisationFieldsetAdditional = () => {
  const form = useFormContext<InstitutionFuturesClientCategorisationAdditionalOrDefaults>();
  const { setValue, watch } = form;
  const { t } = useTranslation();
  const primaryQuestionNoCount = [watch('hasBalanceSheetAssetsAboveAmount'), watch('hasNetTurnoverAboveAmount'), watch('hasOwnFundsAboveAmount')].filter(
    (value) => value === 'NO',
  ).length;
  const hasRepresentativeWorkedInFinancialSector = watch('hasRepresentativeWorkedInFinancialSector');

  useEffect(() => {
    if (primaryQuestionNoCount < CLIENT_CATEGORISATION_SECONDARY_QUESTIONS_NO_COUNT_THRESHOLD) {
      setValue('hasRepresentativeWorkedInFinancialSector', undefined);
      setValue('hasRepresentativeKnowledgeOfTransactions', undefined);
      setValue('hasPortfolioOverAmount', undefined);
      setValue('hasRepresentativeTransactions', undefined);
    }
  }, [primaryQuestionNoCount, setValue]);

  return (
    <div className="space-y-6">
      <FormField
        name="hasBalanceSheetAssetsAboveAmount"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.clientCategorisation.hasBalanceSheetAssetsAboveAmount')}*</FormLabel>
            <FormControl>
              <RadioGroup
                defaultValue={field.value}
                variant="cards"
                aria-label={t('institutionDetails.clientCategorisation.hasBalanceSheetAssetsAboveAmount')}
                onValueChange={field.onChange}
              >
                {BINARY_ANSWERS.map((answer) => (
                  <FormItem key={answer}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={answer} />
                      </FormControl>
                      <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="hasNetTurnoverAboveAmount"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.clientCategorisation.hasNetTurnoverAboveAmount')}*</FormLabel>
            <FormControl>
              <RadioGroup
                defaultValue={field.value}
                variant="cards"
                aria-label={t('institutionDetails.clientCategorisation.hasNetTurnoverAboveAmount')}
                onValueChange={field.onChange}
              >
                {BINARY_ANSWERS.map((answer) => (
                  <FormItem key={answer}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={answer} />
                      </FormControl>
                      <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="hasOwnFundsAboveAmount"
        control={form.control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('institutionDetails.clientCategorisation.hasOwnFundsAboveAmount')}*</FormLabel>
            <FormControl>
              <RadioGroup
                defaultValue={field.value}
                variant="cards"
                aria-label={t('institutionDetails.clientCategorisation.hasOwnFundsAboveAmount')}
                onValueChange={field.onChange}
              >
                {BINARY_ANSWERS.map((answer) => (
                  <FormItem key={answer}>
                    <RadioGroupItemCard variant="outline">
                      <FormControl>
                        <RadioGroupItem value={answer} />
                      </FormControl>
                      <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                    </RadioGroupItemCard>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {primaryQuestionNoCount >= CLIENT_CATEGORISATION_SECONDARY_QUESTIONS_NO_COUNT_THRESHOLD ? (
        <>
          <FormField
            name="hasRepresentativeWorkedInFinancialSector"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.clientCategorisation.hasRepresentativeWorkedInFinancialSector')}</FormLabel>
                <FormControl>
                  <RadioGroup
                    defaultValue={field.value}
                    variant="cards"
                    aria-label={t('institutionDetails.clientCategorisation.hasRepresentativeWorkedInFinancialSector')}
                    onValueChange={(value) => {
                      field.onChange(value);
                      if (value === 'NO') setValue('hasRepresentativeKnowledgeOfTransactions', undefined);
                    }}
                  >
                    {BINARY_ANSWERS.map((answer) => (
                      <FormItem key={answer}>
                        <RadioGroupItemCard variant="outline">
                          <FormControl>
                            <RadioGroupItem value={answer} />
                          </FormControl>
                          <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                        </RadioGroupItemCard>
                      </FormItem>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {hasRepresentativeWorkedInFinancialSector === 'YES' ? (
            <FormField
              name="hasRepresentativeKnowledgeOfTransactions"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('institutionDetails.clientCategorisation.hasRepresentativeKnowledgeOfTransactions')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      defaultValue={field.value}
                      variant="cards"
                      aria-label={t('institutionDetails.clientCategorisation.hasRepresentativeKnowledgeOfTransactions')}
                      onValueChange={field.onChange}
                    >
                      {BINARY_ANSWERS.map((answer) => (
                        <FormItem key={answer}>
                          <RadioGroupItemCard variant="outline">
                            <FormControl>
                              <RadioGroupItem value={answer} />
                            </FormControl>
                            <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                          </RadioGroupItemCard>
                        </FormItem>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}
          <FormField
            name="hasPortfolioOverAmount"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.clientCategorisation.hasPortfolioOverAmount')}</FormLabel>
                <FormControl>
                  <RadioGroup
                    defaultValue={field.value}
                    variant="cards"
                    aria-label={t('institutionDetails.clientCategorisation.hasPortfolioOverAmount')}
                    onValueChange={field.onChange}
                  >
                    {BINARY_ANSWERS.map((answer) => (
                      <FormItem key={answer}>
                        <RadioGroupItemCard variant="outline">
                          <FormControl>
                            <RadioGroupItem value={answer} />
                          </FormControl>
                          <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                        </RadioGroupItemCard>
                      </FormItem>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="hasRepresentativeTransactions"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.clientCategorisation.hasRepresentativeTransactions')}</FormLabel>
                <FormControl>
                  <RadioGroup
                    defaultValue={field.value}
                    variant="cards"
                    aria-label={t('institutionDetails.clientCategorisation.hasRepresentativeTransactions')}
                    onValueChange={field.onChange}
                  >
                    {BINARY_ANSWERS.map((answer) => (
                      <FormItem key={answer}>
                        <RadioGroupItemCard variant="outline">
                          <FormControl>
                            <RadioGroupItem value={answer} />
                          </FormControl>
                          <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                        </RadioGroupItemCard>
                      </FormItem>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      ) : null}
    </div>
  );
};
InstitutionFuturesClientCategorisationFieldsetAdditional.displayName = 'InstitutionFuturesClientCategorisationFieldsetAdditional';

export { InstitutionFuturesClientCategorisationFieldsetAdditional, InstitutionFuturesClientCategorisationFieldsetAdditionalLegend };
